import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Users as UsersIcon, Plus, Trash2, Pencil, KeyRound } from 'lucide-react';
import { ResetPasswordDialog } from '../components/ResetPasswordDialog';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';


const roleNames = {
  purchasing: 'Purchasing',
  staff_gudang: 'Staff Gudang',
  staff_clerk: 'Staff Clerk',
  account_payable: 'Account Payable',
  internal: 'Internal',
  operasional: 'Operasional',
  approval: 'Approval',
  admin: 'Admin'
};

const roleColors = {
  purchasing: 'bg-blue-100 text-blue-700',
  staff_gudang: 'bg-orange-100 text-orange-700',
  staff_clerk: 'bg-amber-100 text-amber-800',
  account_payable: 'bg-emerald-100 text-emerald-700',
  internal: 'bg-purple-100 text-purple-700',
  operasional: 'bg-[#f6dede] text-[#800000]',
  approval: 'bg-indigo-100 text-indigo-700',
  admin: 'bg-slate-100 text-slate-700'
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'purchasing', nomor_rekening: '', nama_bank: '', nomor_wa: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [editUser, setEditUser] = useState(null); // user yang sedang diedit
  const [editLoading, setEditLoading] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Access denied');
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error(`Gagal memuat users${error.response?.data?.detail ? ': ' + error.response.data.detail : error.response?.status ? ' (' + error.response.status + ')' : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.post(`/auth/register`, newUser);
      toast.success('User berhasil ditambahkan');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'purchasing', nomor_rekening: '', nama_bank: '', nomor_wa: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan user');
    } finally {
      setAddLoading(false);
    }
  };

  const handleToggleActive = async (targetUser) => {
    setTogglingId(targetUser.id);
    try {
      const res = await api.patch(`/users/${targetUser.id}/toggle-active`);
      toast.success(res.data.message);
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, is_active: res.data.is_active } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah status user');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setEditLoading(true);
    try {
      const res = await api.put(`/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
        ...(editUser.role === 'approval' ? { approval_scope: editUser.approval_scope || 'semua' } : {}),
        nomor_rekening: editUser.nomor_rekening || '',
        nama_bank: editUser.nama_bank || '',
        nomor_wa: editUser.nomor_wa || '',
      });
      setUsers(prev => prev.map(u => (u.id === editUser.id ? res.data : u)));
      toast.success('Data user diperbarui');
      setEditUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('User berhasil dihapus');
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-slate-600">Access denied. Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-8 w-8 text-red-600" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>User Management</h1>
          </div>
          <div className="flex gap-2">
            <ExportMenu table="users" size="default" />
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah User
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Daftar Pengguna</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">Name</th>
                      <th className="px-4 py-3 text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-sm font-semibold">Role</th>
                      <th className="px-4 py-3 text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-sm font-semibold">Created At</th>
                      <th className="px-4 py-3 text-sm font-semibold">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isSelf = u.id === user?.id;
                      return (
                        <tr key={u.id} className={`border-b ${!u.is_active ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                          <td className="px-4 py-3 font-semibold">
                            {u.name}
                            {u.must_change_password && (
                              <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800" data-testid={`must-change-badge-${u.id}`}>Menunggu ganti password</span>
                            )}
                          </td>
                          <td className="px-4 py-3">{u.email}</td>
                          <td className="px-4 py-3">
                            <span className={`px-3 py-1 rounded-md text-xs font-medium ${roleColors[u.role]}`}>
                              {roleNames[u.role] || u.role || "-"}
                            </span>
                            {u.role === 'approval' && (
                              <span className={`ml-1 px-2 py-0.5 rounded text-[10px] font-semibold ${u.approval_scope === 'operasional' ? 'bg-[#f6dede] text-[#800000]' : u.approval_scope === 'umum' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`scope-badge-${u.id}`}>
                                {u.approval_scope === 'operasional' ? 'Operasional' : u.approval_scope === 'umum' ? 'Umum' : 'Semua'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={u.is_active}
                                onCheckedChange={() => handleToggleActive(u)}
                                disabled={isSelf || togglingId === u.id}
                                data-testid={`toggle-active-${u.id}`}
                              />
                              <span className={`text-xs font-medium ${u.is_active ? 'text-emerald-700' : 'text-slate-500'}`}>
                                {u.is_active ? 'Aktif' : 'Nonaktif'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {new Date(u.created_at).toLocaleDateString('id-ID')}
                          </td>
                          <td className="px-4 py-3 flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditUser({ ...u })}
                              title="Edit nama, peran, dan data bank"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPasswordTarget(u)}
                              title="Ganti password"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
                              disabled={isSelf}
                              title={isSelf ? 'Tidak dapat menghapus akun sendiri' : 'Hapus user'}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Belum ada user</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-900">Role Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(roleNames).map(([key, name]) => {
                const count = users.filter(u => u.role === key).length;
                return (
                  <div key={key} className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="text-sm text-slate-600">{name}</p>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Add User Dialog */}
        <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Tambah User Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-3">
              <div className="space-y-1">
                <Label>Nama Lengkap</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required data-testid="new-user-name" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="new-user-email" />
              </div>
              <div className="space-y-1">
                <Label>Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required data-testid="new-user-password" />
              </div>
              <div className="space-y-1">
                <Label>Role</Label>
                <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} data-testid="new-user-role">
                  <option value="purchasing">Purchasing</option>
                  <option value="staff_gudang">Staff Gudang</option>
                  <option value="staff_clerk">Staff Clerk</option>
                  <option value="account_payable">Account Payable</option>
                  <option value="internal">Internal</option>
                  <option value="operasional">Operasional</option>
                  <option value="approval">Approval</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
                {newUser.role === 'approval' && (
                  <div className="space-y-1">
                    <Label>Cakupan Approval</Label>
                    <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={newUser.approval_scope || 'semua'} onChange={(e) => setNewUser({...newUser, approval_scope: e.target.value})} data-testid="new-user-scope">
                      <option value="semua">Semua — Invoice, DP, IPR, OPR & semua antrian bank</option>
                      <option value="umum">Umum — Invoice, DP, IPR, antrian BSI/BRI</option>
                      <option value="operasional">Operasional — hanya OPR, antrian BSI Ops/BRI Ops</option>
                    </select>
                    <p className="text-xs text-slate-500">Menentukan data yang terlihat di Payment Requests, Proses Bank, Upload Bukti, Tipe Request & Data Penerima.</p>
                  </div>
                )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>No. Rekening</Label>
                  <Input value={newUser.nomor_rekening} onChange={(e) => setNewUser({...newUser, nomor_rekening: e.target.value})} placeholder="Opsional" />
                </div>
                <div className="space-y-1">
                  <Label>Nama Bank</Label>
                  <Input value={newUser.nama_bank} onChange={(e) => setNewUser({...newUser, nama_bank: e.target.value})} placeholder="Opsional" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>No. WA</Label>
                <Input value={newUser.nomor_wa} onChange={(e) => setNewUser({...newUser, nomor_wa: e.target.value})} placeholder="Opsional" />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={addLoading} data-testid="submit-new-user">
                {addLoading ? 'Menyimpan...' : 'Tambah User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User — email dan password sengaja tidak ada di sini:
            email adalah identitas login, password punya aturan sendiri. */}
        <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              {editUser && <p className="text-sm text-slate-600">{editUser.email}</p>}
            </DialogHeader>
            {editUser && (
              <form onSubmit={handleSaveEdit} className="space-y-3">
                <div className="space-y-1">
                  <Label>Nama Lengkap</Label>
                  <Input
                    value={editUser.name}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                    placeholder="Nama asli pengguna"
                    required
                    data-testid="edit-user-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Role</Label>
                  <select
                    className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                    value={editUser.role}
                    onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                    disabled={editUser.id === user?.id}
                    data-testid="edit-user-role"
                  >
                    {Object.entries(roleNames).map(([key, name]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                  {editUser.id === user?.id && (
                    <p className="text-xs text-slate-500">Peran akun sendiri tidak dapat diubah.</p>
                  )}
                </div>
                {editUser.role === 'approval' && (
                  <div className="space-y-1">
                    <Label>Cakupan Approval</Label>
                    <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={editUser.approval_scope || 'semua'} onChange={(e) => setEditUser({ ...editUser, approval_scope: e.target.value })} data-testid="edit-user-scope">
                      <option value="semua">Semua — Invoice, DP, IPR, OPR & semua antrian bank</option>
                      <option value="umum">Umum — Invoice, DP, IPR, antrian BSI/BRI</option>
                      <option value="operasional">Operasional — hanya OPR, antrian BSI Ops/BRI Ops</option>
                    </select>
                    <p className="text-xs text-slate-500">Menentukan data yang terlihat di Payment Requests, Proses Bank, Upload Bukti, Tipe Request & Data Penerima.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>No. Rekening</Label>
                    <Input
                      value={editUser.nomor_rekening || ''}
                      onChange={(e) => setEditUser({ ...editUser, nomor_rekening: e.target.value })}
                      placeholder="Opsional"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Nama Bank</Label>
                    <Input
                      value={editUser.nama_bank || ''}
                      onChange={(e) => setEditUser({ ...editUser, nama_bank: e.target.value })}
                      placeholder="Opsional"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>No. WA</Label>
                  <Input
                    value={editUser.nomor_wa || ''}
                    onChange={(e) => setEditUser({ ...editUser, nomor_wa: e.target.value })}
                    placeholder="Opsional"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditUser(null)}>Batal</Button>
                  <Button type="submit" disabled={editLoading} data-testid="edit-user-save">
                    {editLoading ? 'Menyimpan...' : 'Simpan'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <ResetPasswordDialog
          target={passwordTarget}
          isSelf={passwordTarget?.id === user?.id}
          onClose={() => { setPasswordTarget(null); fetchUsers(); }}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteUser}
          title="Hapus User"
          description="Apakah Anda yakin ingin menghapus user ini? Tindakan ini tidak dapat dibatalkan."
          itemName={userToDelete ? `${userToDelete.name} (${userToDelete.email})` : ''}
        />
      </div>
    </Layout>
  );
}



